import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Navbar from '../components/Navbar';
import SearchBar from '../components/SearchBar';
import EvidenceCard from '../components/EvidenceCard';
import SkepticsCorner from '../components/SkepticsCorner';
import DonationFeedbackModal from '../components/DonationFeedbackModal';
import ShareModal from '../components/ShareModal';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Star, Download, Lightbulb, ArrowRight, Brain, Target, Shield, Stethoscope, Pill, FlaskConical, MapPin, Share2, MessageCircle, RotateCcw, MessageSquare, Loader2, X, ThumbsUp, ThumbsDown } from 'lucide-react';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showDonation, setShowDonation] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [followUpQuery, setFollowUpQuery] = useState('');
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [inlineRating, setInlineRating] = useState(null); // 'up' | 'down' | null
  const searchBarRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    loadUsage();
    // Track page view
    api.trackEvent('page_view', { page: 'dashboard' }).catch(() => {});
  }, []);

  const loadUsage = async () => {
    try {
      const response = await api.checkUsage();
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to load usage:', err);
    }
  };

  const handleSearch = async (query) => {
    setIsSearching(true);
    setIsTransitioning(true);
    setSearchResult(null);
    setFollowUpQuery('');
    setDosingInfo(null);
    setInlineRating(null);

    try {
      const response = await api.search(query);
      setSearchResult(response.data);
      loadUsage();
      
      // Clear the search bar after successful search
      if (searchBarRef.current) {
        searchBarRef.current.clearQuery();
      }
      
      // Smooth scroll to results after a brief delay
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      // Track search event
      api.trackEvent('search', { query_length: query.length }).catch(() => {});
      
      // Show donation prompt if indicated
      if (response.data.show_donation_prompt) {
        setTimeout(() => setShowDonation(true), 1000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed. Please try again.');
    } finally {
      setIsSearching(false);
      setIsTransitioning(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUpQuery.trim() || !searchResult) return;
    
    setIsFollowUp(true);
    setIsSearching(true);
    setIsTransitioning(true);
    setDosingInfo(null);
    setInlineRating(null);

    try {
      const response = await api.searchFollowup(followUpQuery, searchResult.id);
      setSearchResult(response.data);
      setFollowUpQuery('');
      loadUsage();
      
      // Smooth scroll to results section
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      api.trackEvent('follow_up_search').catch(() => {});
      
      if (response.data.show_donation_prompt) {
        setTimeout(() => setShowDonation(true), 1000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Follow-up search failed.');
    } finally {
      setIsSearching(false);
      setIsFollowUp(false);
      setIsTransitioning(false);
    }
  };

  const handleNewSearch = () => {
    setSearchResult(null);
    setFollowUpQuery('');
    setDosingInfo(null);
    setInlineRating(null);
    // Clear the search bar
    if (searchBarRef.current) {
      searchBarRef.current.clearQuery();
    }
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [dosingInfo, setDosingInfo] = useState(null);
  const [loadingDosing, setLoadingDosing] = useState(false);

  const handleGetDosing = async () => {
    if (!searchResult) return;
    
    setLoadingDosing(true);
    try {
      // Use follow-up to get dosing details
      const dosingQuery = "Please provide specific medication dosing details including: drug names, doses (mg/kg or fixed), route of administration, frequency, and typical duration of treatment for the recommended therapies.";
      const response = await api.searchFollowup(dosingQuery, searchResult.id);
      
      // Extract dosing info from response - ensure we get string content
      const keyRec = response.data.response?.key_recommendation;
      const logicModel = response.data.response?.logic_model;
      
      setDosingInfo({
        content: typeof keyRec === 'string' ? keyRec : JSON.stringify(keyRec) || "Dosing information retrieved.",
        logicModel: typeof logicModel === 'string' ? logicModel : null
      });
      
      toast.success('Dosing details loaded');
    } catch (err) {
      toast.error('Failed to load dosing details');
    } finally {
      setLoadingDosing(false);
    }
  };

  const handleStar = async () => {
    if (!searchResult) return;
    try {
      const response = await api.toggleStar(searchResult.id);
      setSearchResult(prev => ({ ...prev, is_starred: response.data.is_starred }));
      toast.success(response.data.is_starred ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  const handleExport = async () => {
    if (!searchResult) return;
    try {
      const response = await api.exportQuery(searchResult.id);
      const blob = new Blob([response.data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Summary exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleInlineRating = async (rating) => {
    if (!searchResult) return;
    
    // Toggle if clicking the same rating
    const newRating = inlineRating === rating ? null : rating;
    setInlineRating(newRating);
    
    if (newRating) {
      try {
        // Send quick feedback with accuracy rating based on thumbs
        await api.submitQuickFeedback(
          null, // no app rating
          newRating === 'up' ? 5 : 2, // accuracy: thumbs up = 5, thumbs down = 2
          null // no comment
        );
        toast.success(newRating === 'up' ? 'Thanks for the feedback!' : 'Thanks - we\'ll work to improve');
        api.trackEvent('inline_rating', { rating: newRating, query_id: searchResult.id }).catch(() => {});
      } catch (err) {
        // Silent fail - don't interrupt UX for feedback errors
        console.error('Failed to submit inline rating:', err);
      }
    }
  };

  const remainingQueries = usage ? Math.max(0, usage.queries_limit - usage.queries_today) : 5;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      <Navbar />
      <main className="pt-20 pb-16 main-content">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/images/logo.png" 
              alt="NoSpin-EBM" 
              className="w-12 h-12 object-contain"
            />
            <h1 className="text-4xl md:text-5xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
              NoSpin-EBM
            </h1>
            {/* Country Flag - Inline with header */}
            {user && (
              <CountryFlag 
                country={user.country} 
                province={user.province}
                size="small"
                className="ml-2"
              />
            )}
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">(Evidence-Based Medicine)</p>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
            AI-powered clinical insights you can trust. Data-backed recommendations without the spin.
          </p>

          <SearchBar 
            ref={searchBarRef}
            onSearch={handleSearch} 
            isSearching={isSearching && !isFollowUp}
            disabled={false}
          />

          {/* Usage indicator */}
          {usage && (
            <div className="mt-4 flex flex-col items-center gap-3">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="font-medium">{remainingQueries}</span>
                <span>free searches remaining today</span>
                <span className="text-slate-400 dark:text-slate-500">•</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">Always free</span>
                {usage.donation_status?.bypass_prompts && (
                  <span className="ml-2 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-full text-xs">
                    {usage.is_ambassador ? 'Ambassador' : 'Supporter'}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Loading State for Transition */}
        {isTransitioning && isSearching && (
          <section className="max-w-4xl mx-auto px-4 pb-8" data-testid="loading-section">
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-slate-400 dark:text-slate-500 mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {isFollowUp ? 'Getting follow-up answer...' : 'Deep thinking, takes a bit more time...'}
              </p>
            </div>
          </section>
        )}

        {/* Results Section */}
        {searchResult && !isTransitioning && (
          <section 
            ref={resultsRef}
            className="max-w-4xl mx-auto px-4 pb-8 animate-in fade-in duration-300" 
            data-testid="results-section"
          >
            {/* Actions - Mobile responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">Evidence Summary</h2>
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-start sm:justify-end">
                {/* Inline Rating */}
                <div className="flex items-center gap-1" data-testid="inline-rating">
                  <Button
                    variant={inlineRating === 'up' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleInlineRating('up')}
                    className={`h-8 w-8 p-0 rounded-full ${inlineRating === 'up' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'text-slate-400 hover:text-emerald-500'}`}
                    data-testid="thumbs-up-btn"
                    title="Helpful"
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={inlineRating === 'down' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleInlineRating('down')}
                    className={`h-8 w-8 p-0 rounded-full ${inlineRating === 'down' ? 'bg-red-500 hover:bg-red-600 text-white' : 'text-slate-400 hover:text-red-500'}`}
                    data-testid="thumbs-down-btn"
                    title="Not helpful"
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowShare(true)}
                  className="rounded-full h-8 w-8 p-0 sm:w-auto sm:px-3"
                  data-testid="share-button"
                  title="Share"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStar}
                  className="rounded-full h-8 w-8 p-0 sm:w-auto sm:px-3"
                  data-testid="star-button"
                  title="Star"
                >
                  <Star className={`h-4 w-4 ${searchResult.is_starred ? 'fill-amber-400 text-amber-400' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  className="rounded-full h-8 px-2 sm:px-3"
                  data-testid="export-button"
                  title="Export"
                >
                  <Download className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </div>
            </div>

            {/* Key Recommendation */}
            <Card className="mb-6 border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-900/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-sky-100 dark:bg-sky-900/50">
                    <Target className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Key Recommendation</h3>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                      {searchResult.response?.key_recommendation || 'No recommendation available'}
                    </p>
                    
                    {/* Dosing Button */}
                    {!dosingInfo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGetDosing}
                        disabled={loadingDosing}
                        className="mt-4 rounded-full border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                        data-testid="get-dosing-btn"
                      >
                        {loadingDosing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading dosing...
                          </>
                        ) : (
                          <>
                            <Pill className="h-4 w-4 mr-2" />
                            Get Dosing Details
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dosing Information */}
            {dosingInfo && (
              <Card className="mb-6 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                      <Pill className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Medication Dosing</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDosingInfo(null)}
                          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                        {dosingInfo.content}
                      </p>
                      {dosingInfo.logicModel && (
                        <div className="mt-4 p-3 bg-white/50 dark:bg-slate-700/50 rounded-lg border border-purple-100 dark:border-purple-800">
                          <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-1">Dosing Rationale</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{dosingInfo.logicModel}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Local Context */}
            {searchResult.response?.local_context && (
              <Card className="mb-6 border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/50">
                      <MapPin className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                        Local Context: {searchResult.response.local_context.region}
                      </h3>
                      <div className="space-y-3">
                        {searchResult.response.local_context.local_guidelines && (
                          <div>
                            <h4 className="text-sm font-medium text-teal-800 dark:text-teal-300 mb-1">Local Guidelines</h4>
                            <p className="text-sm text-slate-700 dark:text-slate-300">{searchResult.response.local_context.local_guidelines}</p>
                          </div>
                        )}
                        {searchResult.response.local_context.local_epidemiology && (
                          <div>
                            <h4 className="text-sm font-medium text-teal-800 dark:text-teal-300 mb-1">Local Epidemiology</h4>
                            <p className="text-sm text-slate-700 dark:text-slate-300">{searchResult.response.local_context.local_epidemiology}</p>
                          </div>
                        )}
                        {searchResult.response.local_context.local_resources && (
                          <div>
                            <h4 className="text-sm font-medium text-teal-800 dark:text-teal-300 mb-1">Local Resources</h4>
                            <p className="text-sm text-slate-700 dark:text-slate-300">{searchResult.response.local_context.local_resources}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Diagnostic Approach */}
            {searchResult.response?.diagnostic_approach && (
              <Card className="mb-6 border-slate-200 dark:border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                      <Stethoscope className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Diagnostic Approach</h3>
                      
                      {searchResult.response.diagnostic_approach.initial_workup?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Initial Workup</h4>
                          <div className="space-y-1">
                            {searchResult.response.diagnostic_approach.initial_workup.map((item, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                                <FlaskConical className="h-4 w-4 text-purple-400 dark:text-purple-500 mt-0.5 shrink-0" />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {searchResult.response.diagnostic_approach.differential_considerations?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Differential Considerations</h4>
                          <div className="flex flex-wrap gap-2">
                            {searchResult.response.diagnostic_approach.differential_considerations.map((item, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md text-xs">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Treatment Options */}
            {searchResult.response?.treatment_options?.length > 0 && (
              <Card className="mb-6 border-slate-200 dark:border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                      <Pill className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Treatment Options</h3>
                      <div className="space-y-4">
                        {searchResult.response.treatment_options.map((treatment, i) => (
                          <div key={i} className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-bold flex items-center justify-center">
                                {i + 1}
                              </span>
                              <h4 className="font-medium text-slate-900 dark:text-slate-100">{treatment.option}</h4>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{treatment.rationale}</p>
                            {treatment.considerations && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 italic mb-2">
                                <span className="font-medium">Consider:</span> {treatment.considerations}
                              </p>
                            )}
                            {treatment.guideline_source && (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                Source: {treatment.guideline_source}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Logic Model */}
            {searchResult.response?.logic_model && (
              <Card className="mb-6 border-slate-200 dark:border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700">
                      <Brain className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Logic Model</h3>
                      <div className="space-y-2">
                        {searchResult.response.logic_model.steps?.map((step, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                            <ArrowRight className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />
                            <span>{step}</span>
                          </div>
                        ))}
                      </div>
                      {searchResult.response.logic_model.failure_point && (
                        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-700">
                          <p className="text-sm text-amber-800 dark:text-amber-300">
                            <strong>Logic Failure Point:</strong> {searchResult.response.logic_model.failure_point}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Evidence Cards */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                Supporting Evidence
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {searchResult.response?.evidence_cards?.map((card, i) => (
                  <EvidenceCard key={i} card={card} />
                ))}
              </div>
            </div>

            {/* Skeptic's Corner */}
            <SkepticsCorner points={searchResult.response?.skeptics_corner} />

            {/* Follow-up Question */}
            <Card className="mt-6 border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  <h4 className="font-medium text-slate-900 dark:text-slate-100">Continue the conversation</h4>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={followUpQuery}
                    onChange={(e) => setFollowUpQuery(e.target.value)}
                    placeholder="Ask a follow-up question, e.g., What about patients with renal impairment?"
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && handleFollowUp()}
                    disabled={isSearching}
                    data-testid="followup-input"
                  />
                  <Button
                    onClick={handleFollowUp}
                    disabled={!followUpQuery.trim() || isSearching}
                    className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="followup-btn"
                  >
                    {isSearching && isFollowUp ? 'Searching...' : 'Ask'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons at Bottom */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={handleNewSearch}
                className="w-full sm:w-auto rounded-full px-8 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="bottom-new-search"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Start Fresh Search
              </Button>
              <Button
                onClick={() => document.querySelector('[data-testid="followup-input"]')?.focus()}
                variant="outline"
                className="w-full sm:w-auto rounded-full px-6"
                data-testid="bottom-followup"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Ask Follow-up
              </Button>
            </div>

            {/* Feedback Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Was this helpful?</p>
              <Button
                onClick={() => window.location.href = `/feedback?query_id=${searchResult.id}`}
                variant="ghost"
                size="sm"
                className="text-sky-600 hover:text-sky-700"
                data-testid="result-feedback-btn"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Give Feedback
              </Button>
            </div>
          </section>
        )}

        {/* Empty State */}
        {!searchResult && !isSearching && (
          <section className="max-w-4xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Target, title: 'Data-Driven', desc: 'Prioritizes RCT evidence over dogma' },
                { icon: Shield, title: 'GRADE Ratings', desc: 'Every claim has evidence quality' },
                { icon: Lightbulb, title: 'Critical View', desc: "Skeptic's Corner challenges assumptions" }
              ].map((item, i) => (
                <Card key={i} className="border-slate-200">
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <item.icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>

      <DisclaimerBanner />
      <DonationFeedbackModal 
        open={showDonation} 
        onClose={() => setShowDonation(false)} 
        onBypass={() => loadUsage()}
      />
      <ShareModal 
        open={showShare} 
        onClose={() => setShowShare(false)} 
        queryId={searchResult?.id}
        queryText={searchResult?.query_text}
      />
    </div>
  );
};

export default DashboardPage;
